import React from 'react';
import { Chip, Paper, Typography, TableContainer, Table, TableBody, TableCell, TableRow, Card, CardHeader, Stack, Skeleton, Box } from '@mui/material';
import { Group, Person, SellOutlined } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import renderPercentage from '../renderPercentage';
import GroupTypeChip from '../../contacts/GroupTypeChip';
import ThemedChip from '../../themedChip/ThemedChip';
import { lighten } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import TypedValue from '../../TypedValue';
import RichTooltip from '../../RichTooltip';
import ReportsCardContent from './ReportsCardContent';

const Figure = ({ label, figure }) => {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1,
                textAlign: 'center',
                height: '100%'
            }}
        >
            <Typography variant="h4" color="primary">
                <TypedValue value={figure} type="number" />
            </Typography>
            <Typography
                variant="body1"
            >
                {label}
            </Typography>
        </Paper>
    );
};

const Body = ({ bestGroup }) => {
    const theme = useTheme();

    if (!bestGroup) {
        return (
            <Grid container spacing={2}>
                <Grid size={6}>
                    <Skeleton height={100} />
                </Grid>
                <Grid size={6}>
                    <Skeleton height={100} />
                </Grid>
                <Grid size={12}>
                    <Skeleton height={60} />
                </Grid>
                <Grid size={3}>
                    <Skeleton height={100} />
                </Grid>
                <Grid size={3}>
                    <Skeleton height={100} />
                </Grid>
                <Grid size={3}>
                    <Skeleton height={100} />
                </Grid>
                <Grid size={3}>
                    <Skeleton height={100} />
                </Grid>
            </Grid>
        );
    }

    if (bestGroup.error) {
        let message = 'Insufficient Data';

        switch (bestGroup.error) {
            case 'groups':
                message = 'Insufficient Data: No Groups found';
                break;
            case 'links':
                message = 'Insufficient Data: Email has no links';
                break;
            case 'actions':
                message = 'Insufficient Data: Campaign too old';
                break;
            case 'clicks':
                message = 'Insufficient Data: No recipient engagement';
                break;
        }

        return (
            <Typography variant="body1">
                {message}
            </Typography>
        );
    }

    return (
        <Stack spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                px: 2,
                                py: 1,
                                backgroundColor: lighten(theme.palette.primary.main, 0.9),
                                height: '100%'
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, wordBreak: 'break-word' }}
                            >
                                {bestGroup.name}
                            </Typography>
                            <Stack
                                direction="row"
                                sx={{
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mt: 1
                                }}
                            >
                                <GroupTypeChip type={bestGroup.type} />
                                <Chip
                                    size="small"
                                    icon={<Person fontSize="small" />}
                                    label={`${bestGroup.contacts?.toLocaleString() ?? 0} Contacts`}
                                    variant="outlined"
                                />
                            </Stack>
                            {bestGroup.tags.length > 0 && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mt: 1
                                    }}
                                >
                                    {bestGroup.tags.map(tag => (
                                        <ThemedChip
                                            key={tag.id}
                                            chipProps={{
                                                size: 'small',
                                                label: tag.name,
                                                icon: <SellOutlined fontSize="small" />
                                            }}
                                            colour={tag.colour}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Grid>
                    {bestGroup.bestContact !== null && (
                        <Grid size={{ xs: 12, lg: 6 }}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 2,
                                    py: 1,
                                    height: '100%'
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                >
                                    Best Contact in Group:
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 0.5,
                                        mt: 0.5
                                    }}
                                >
                                    <Person fontSize="small" color="primary" />
                                    <Typography
                                        variant="body1"
                                        color="primary"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        {bestGroup.bestContact.firstName} {bestGroup.bestContact.lastName}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="caption"
                                >
                                    {bestGroup.bestContact.emailAddress}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                    <Typography
                                        variant="body2"
                                    >
                                        Opens: <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}><TypedValue value={bestGroup.bestContact.opens} type="number" accuracy={0} /></Box>
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                    >
                                        Clicks: <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}><TypedValue value={bestGroup.bestContact.clicks} type="number" accuracy={0} /></Box>
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Box>
            <Paper variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell variant="head">
                                    Unique Opens
                                </TableCell>
                                <TableCell align="right" width="30%">
                                    <Typography variant="h6" color="primary">
                                        <TypedValue value={bestGroup.uniqueOpens} type="number" accuracy={0} />
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" width="20%">
                                    {renderPercentage(bestGroup.uniqueOpens, bestGroup.contacts)}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell variant="head">
                                    Unique Clicks
                                </TableCell>
                                <TableCell align="right" width="30%">
                                    <Typography variant="h6" color="primary">
                                        <TypedValue value={bestGroup.uniqueClicks} type="number" accuracy={0} />
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" width="20%">
                                    {renderPercentage(bestGroup.uniqueClicks, bestGroup.contacts)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Figure
                            label="Mean Average Opens"
                            figure={bestGroup.averageOpens}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Figure
                            label="Mean Average Clicks"
                            figure={bestGroup.averageClicks}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Figure
                            label="Trimmed Mean Average Opens"
                            explanation="Mean opens of those who opened, ignoring non-openers"
                            figure={bestGroup.averageOpensOfThoseWhoOpened}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Figure
                            label="Trimmed Mean Average Clicks"
                            explanation="Mean clicks of those who clicked, ignoring non-clickers"
                            figure={bestGroup.averageClicksOfThoseWhoClicked}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Stack>
    );
};

const BestPerformingGroup = ({ bestGroup }) => {
    return (
        <Card sx={{ height: '100%' }}>
            <CardHeader
                sx={{
                    m: 0,
                    py: 1,
                    '& .MuiCardHeader-action': {
                        alignSelf: 'center'
                    }
                }}
                slotProps={{
                    title: {
                        variant: 'h6',
                        fontWeight: 'normal'
                    }
                }}
                avatar={<Group />}
                title="Best Performing Group"
                action={(
                    <RichTooltip inline={true}>
                        <Typography
                            variant="body2"
                        >
                            A 'trimmed mean' is an average that does not include zeroes.
                        </Typography>
                        <Typography
                            variant="body2"
                        >
                            In this context, Contacts who did not Open/Click are excluded from those figures
                        </Typography>
                    </RichTooltip>
                )}
            />
            <ReportsCardContent>
                <Body bestGroup={bestGroup} />
            </ReportsCardContent>
        </Card>
    );
};

export default BestPerformingGroup;